import { c as _c } from "react/compiler-runtime";
import "../styles/globals.css";
import type { AppProps } from "next/app";
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import PlausibleProvider from "next-plausible";
import dynamic from "next/dynamic";

// import { usePreviewSubscription } from "../lib/sanity";
import { RouterContext, RouterContextValue, fetchRouterContext } from "../context/RouterContext";
import { getProfileLayoutStaticProps } from "../components/profile/layout/layoutStaticProps";
import { Layout } from "../components/main/layout/layout";
import { FullPageSpinner } from "../components/shared/layout/FullPageSpinner/FullPageSpinner";
import { ConsentState } from "../proxy.page";
import { createWidgetStore, WidgetStoreProvider } from "../components/shared/components/Widget/store/store";
import { useRouter } from "next/router";
import { getReferralCodeFromQuery, storeReferralCode } from "../util/referralCode";
import { useTrackingEnabled } from "../hooks/useTrackingEnabled";
import { createVisibilityHiddenHandler } from "../util/visibilityBeacon";
const PreviewProvider = lazy(() => import("../components/shared/PreviewProvider"));
// The Min side layout (with Auth0) is only loaded on dashboard pages. It is still server-rendered,
// and Next loads its chunk before hydrating. On client-side navigation the placeholder matches
// what the layout itself shows while it checks the login.
const ProfileLayout = dynamic(() => import("../components/profile/layout/layout").then(m => m.ProfileLayout), {
  loading: () => <FullPageSpinner />
});
const VisualEditing = dynamic(() => import("@sanity/visual-editing/next-pages-router").then(mod => mod.VisualEditing), {
  ssr: false
});
// The widget pane's state, kept for the whole visit
const globalWidgetStore = createWidgetStore();
export enum LayoutType {
  Default = "default",
  Profile = "profile",
}
export type GeneralPageProps = Record<string, unknown> & {
  preview: boolean;
  draftMode: boolean;
  token: string | null;
  data?: {
    result: Record<string, unknown>;
    query: string;
    queryParams: {
      slug?: string;
    };
  };
  appStaticProps?: Awaited<ReturnType<typeof getAppStaticProps>>;
};
function MyApp(t0) {
  const $ = _c(40);
  const {
    Component,
    pageProps: t1
  } = t0;
  let appStaticProps;
  let pageProps;
  if ($[0] !== t1) {
    const {
      appStaticProps: t2,
      preview,
      ...t3
    } = t1;
    appStaticProps = t2;
    pageProps = t3;
    $[0] = t1;
    $[1] = appStaticProps;
    $[2] = pageProps;
  } else {
    appStaticProps = $[1];
    pageProps = $[2];
  }
  const tracking = useTrackingEnabled();
  const router = useRouter();
  let t2;
  if ($[3] !== appStaticProps?.routerContext) {
    t2 = () => appStaticProps?.routerContext || null;
    $[3] = appStaticProps?.routerContext;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const [routerContextValue] = useState(t2);
  let t3;
  let t4;
  if ($[5] !== router.isReady || $[6] !== router.query) {
    t3 = () => {
      if (!router.isReady) {
        return;
      }
      const referralCode = getReferralCodeFromQuery(router.query);
      if (referralCode) {
        storeReferralCode(referralCode);
      }
    };
    t4 = [router.isReady, router.query];
    $[5] = router.isReady;
    $[6] = router.query;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  const trackingRef = useRef(tracking);
  let t5;
  let t6;
  if ($[9] !== tracking) {
    t5 = () => {
      trackingRef.current = tracking;
    };
    t6 = [tracking];
    $[9] = tracking;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      if (window.location.hostname === "localhost") {
        return;
      }
      const handleVisibilityChange = createVisibilityHiddenHandler({
        isTrackingEnabled: () => trackingRef.current,
        getVisibilityState: _temp,
        getLocation: _temp2,
        sendBeacon: _temp3
      });
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    t8 = [];
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  useEffect(t7, t8);
  if (!appStaticProps) {
    console.error("appStaticProps is not defined - did you forget to use getAppStaticProps?");
    let t9;
    if ($[14] !== Component || $[15] !== pageProps) {
      t9 = <Component {...pageProps} />;
      $[14] = Component;
      $[15] = pageProps;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    return t9;
  }
  if (pageProps.draftMode && !pageProps.token) {
    console.warn("Draft mode without a Sanity token in pageProps; showing published content");
  }
  if (pageProps.draftMode && pageProps.token) {
    let t9;
    if ($[17] !== Component || $[18] !== appStaticProps.layout || $[19] !== appStaticProps.layoutProps || $[20] !== pageProps) {
      t9 = appStaticProps.layout === LayoutType.Default ? <Layout {...appStaticProps.layoutProps}><Component {...pageProps} /></Layout> : <ProfileLayout {...appStaticProps.layoutProps}><Component {...pageProps} /></ProfileLayout>;
      $[17] = Component;
      $[18] = appStaticProps.layout;
      $[19] = appStaticProps.layoutProps;
      $[20] = pageProps;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    let t10;
    if ($[22] !== routerContextValue || $[23] !== t9) {
      t10 = <WidgetStoreProvider store={globalWidgetStore}><RouterContext.Provider value={routerContextValue}>{t9}</RouterContext.Provider></WidgetStoreProvider>;
      $[22] = routerContextValue;
      $[23] = t9;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    let t11;
    if ($[25] !== t10 || $[26] !== tracking) {
      t11 = <PlausibleProvider enabled={tracking}>{t10}</PlausibleProvider>;
      $[25] = t10;
      $[26] = tracking;
      $[27] = t11;
    } else {
      t11 = $[27];
    }
    let t12;
    if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <Suspense><VisualEditing zIndex={Number.MAX_SAFE_INTEGER} /></Suspense>;
      $[28] = t12;
    } else {
      t12 = $[28];
    }
    let t13;
    if ($[29] !== pageProps.token || $[30] !== t11) {
      t13 = <PreviewProvider token={pageProps.token}>{t11}{t12}</PreviewProvider>;
      $[29] = pageProps.token;
      $[30] = t11;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    return t13;
  }
  let t9;
  if ($[32] !== Component || $[33] !== appStaticProps.layout || $[34] !== appStaticProps.layoutProps || $[35] !== pageProps) {
    t9 = appStaticProps.layout === LayoutType.Default ? <Layout {...appStaticProps.layoutProps}><Component {...pageProps} /></Layout> : <ProfileLayout {...appStaticProps.layoutProps}><Component {...pageProps} /></ProfileLayout>;
    $[32] = Component;
    $[33] = appStaticProps.layout;
    $[34] = appStaticProps.layoutProps;
    $[35] = pageProps;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== routerContextValue || $[38] !== t9) {
    t10 = <PlausibleProvider><WidgetStoreProvider store={globalWidgetStore}><RouterContext.Provider value={routerContextValue}>{t9}</RouterContext.Provider></WidgetStoreProvider></PlausibleProvider>;
    $[37] = routerContextValue;
    $[38] = t9;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  return t10;
}
function _temp3(url, data) {
  return navigator.sendBeacon(url, data);
}
function _temp2() {
  return window.location;
}
function _temp() {
  return document.visibilityState;
}
export async function getAppStaticProps({
  draftMode = false,
  consentState,
  layout = LayoutType.Default,
  showGiveButton = true
}: {
  draftMode: boolean;
  consentState: ConsentState;
  layout?: LayoutType;
  showGiveButton?: boolean;
}) {
  const routerContext = await fetchRouterContext();
  const appStaticProps = {
    routerContext,
    ...(layout === LayoutType.Default ? {
      layout: LayoutType.Default as const,
      layoutProps: await Layout.getStaticProps({
        draftMode,
        consentState,
        showGiveButton
      })
    } : {
      layout: LayoutType.Profile as const,
      layoutProps: await getProfileLayoutStaticProps({
        draftMode,
        consentState
      })
    })
  };
  return appStaticProps;
}
export default MyApp;